import React, {
	useCallback,
	useMemo,
	useState,
} from 'react';
import {
	Col,
	Drawer,
	Flex,
	Form,
	Input,
	Row,
	Space,
	Tag,
} from 'antd';
import {
	DeleteOutlined,
	EditOutlined,
} from '@ant-design/icons';
import {
	useQuery,
	useQueryClient,
} from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faGasPump } from '@fortawesome/pro-light-svg-icons';
import {
	CargoPortCallActivity,
	CargoUnitLabels,
	CargoUnitTypes,
	DATE_AND_TIME,
	PortActionTypeLabels,
	PortActionTypes,
	FixtureTypes,
	TimeZoneFormat,
} from '@shared/utils/constants';
import { capitalize } from '@shared/utils/string';
import {
	getUtcOffset,
	toMoment,
} from '@shared/utils/date';
import { getPortCallActionStatus } from '@shared/utils/getPortCallActionStatus';
import type { GetBunkerStemsResponse } from '@api/features/voyages/bunker-stems/getBunkerStems';
import type { PortActivityProps } from '@api/models/port-activity';
import type { PortCallProps } from '@api/models/port-call';
import Button from '@client/components/Button';
import {
	ItineraryPortCallDto,
	ItineraryPortCallDtoActions,
} from '@client/screens/estimates/details/helpers/types';
import Card from '@client/components/Card/Card';
import Table from '@client/components/Table/Table';
import { formatDate } from '@client/utils/formatDate';
import Select from '@client/components/Select';
import { useVoyage } from '@client/screens/fleet/VoyageDetailsScreen/components/VoyageProvider/VoyageProvider';
import {
	deletePortCallAction,
	getBunkerStems,
	getSuppliers,
	updatePortCall,
} from '@client/lib/api';
import CreateBunkerStemForm from '@client/components/CreateBunkerStemForm/CreateBunkerStemForm';
import { useAuth } from '@client/lib/auth';
import EditableDetails from '@client/components/EditableDetails/EditableDetails';
import showErrorNotification from '@client/utils/showErrorNotification';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import { useItinerary } from '../ItineraryProvider';
import { sortByFuelGrade } from '../utils/helpers';
import PortCallDetails from './PortCallDetails';
import ActivityWithRobsForm from './ActivityWithRobsForm';
import { getBunkerStemTableColumns } from './getColumns';
import PortCallActivities from './PortCallActivities';

const { LocalTime } = TimeZoneFormat;

const PortCallSummaryTab = () => {
	const auth = useAuth();
	const queryClient = useQueryClient();
	const [showActivityModal, setShowActivityModal] = useState(false);
	const [showBunkerDrawer, setShowBunkerDrawer] = useState(false);

	const [editingActionId, setEditingActionId] = useState<number | null>(null);
	const [editingStem, setEditingStem] = useState<
		GetBunkerStemsResponse[number] |
		undefined |
		null
	>();

	const portActivities = useMemo(() => {
		if (!auth.userInfo) {
			return [];
		}

		return auth.userInfo.portActivities;
	}, [auth]);

	const {
		expandedEntry,
		refreshItinerary,
		setExpandedPortCallAction,
	} = useItinerary();

	const {
		cargos,
		voyageDetails,
		refreshDetails,
		refreshVendorExpenses,
	} = useVoyage();

	const selectedPortCall = expandedEntry as ItineraryPortCallDto;

	const {
		id: currentPortCallId,
		cargoActivity: currentActivity,
		cargoCommodity: currentCommodity,
		cargoQuantity: currentQuantity,
		cargoUnits: currentUnits,
		port: currentPort,
		vesselId: currentVesselId,
	} = selectedPortCall || {};

	const [editingCargoActivityValues, setEditingCargoActivityValues] =
		useState<Partial<PortCallProps>>({
			cargoActivity: currentActivity,
			cargoCommodity: currentCommodity,
			cargoQuantity: currentQuantity,
			cargoUnits: currentUnits,
		});

	const {
		data: suppliers,
		isLoading: suppliersLoading,
		refetch: refreshSuppliers,
	} = useQuery('suppliers', getSuppliers, {
		refetchOnWindowFocus: false,
		retry: false,
	});

	const {
		data: bunkerStems,
		isLoading: bunkerStemsLoading,
		refetch: refreshStems,
	} = useQuery(
		['bunkerStems', voyageDetails?.id, currentPortCallId],
		async () => {
			if (!voyageDetails?.id) {
				return [];
			}

			return await getBunkerStems({
				voyageId: voyageDetails.id,
				portCallId: currentPortCallId,
			});
		},
		{
			refetchOnWindowFocus: false,
			retry: false,
			initialData: [],
		},
	);

	const [form] = Form.useForm();

	const { actions } = selectedPortCall;

	const editingAction = useMemo(() => {
		if (editingActionId == null) {
			return null;
		}

		const action = selectedPortCall.actions.find((a) => a.id === editingActionId);

		if (action == null) {
			return null;
		}

		if (action.event === 'loading' || action.event === 'discharging') {
			return action;
		}

		return action;
	}, [selectedPortCall, editingActionId]);

	const onDelete = async (id: number) => {
		await deletePortCallAction(currentVesselId, currentPortCallId, id);
		await refreshItinerary();
		await refreshStems();
		refreshDetails();
		queryClient.invalidateQueries(['cargos']);
	};

	const onClose = async () => {
		queryClient.invalidateQueries(['cargos']);
		setEditingActionId(null);
		await form.resetFields();
		setShowActivityModal(false);
		refreshItinerary();
	};

	const unEditableActions = [
		PortActionTypes.LOADING,
		PortActionTypes.DISCHARGING,
		PortActionTypes.DELIVERY,
		PortActionTypes.REDELIVERY,
		PortActionTypes.REDELIVERING_TC_IN,
		PortActionTypes.COMPLETION,
	];

	const isTcOrBbOut = voyageDetails?.fixture?.type === FixtureTypes.TC_OUT ||
		voyageDetails?.fixture?.type === FixtureTypes.BB_OUT;

	const sortedActions = useMemo(() => {
		// We only want to display activities defined by users in "PortActivities.tsx",
		// but we still need the "loading" activity for labels, filtering etc. - so no need to shows
		// "loading" or "discharging" in the activity table

		const sorted = [...(actions ?? [])].sort((a, b) => {
			if (!a.date && !b.date) {
				return 0;
			}

			if (!a.date) {
				return 1;
			}

			if (!b.date) {
				return -1;
			}

			return a.date.valueOf() - b.date.valueOf();
		});

		return sorted
			.filter(
				(pca) => pca.event !== PortActionTypes.LOADING &&
					pca.event !== PortActionTypes.DISCHARGING,
			);
	}, [actions]);

	const cargoActivity = selectedPortCall ? [
		{
			key: 'cargoActivity',
			label: 'Cargo Activity',
			value: currentActivity,
			type: 'select',
			options: Object.values(CargoPortCallActivity).map((c) => ({ label: c, value: c })),
			editable: true,
			inputProps: { placeholder: 'Select cargo activity', tabIndex: 0 },
		},
		{
			key: 'cargoCommodity',
			label: 'Cargo Commodity',
			value: currentCommodity,
			type: 'text',
			editable: true,
			inputProps: { tabIndex: 0 },
		},
		{
			key: 'cargoQuantity',
			label: 'Cargo Quantity',
			value: editingCargoActivityValues.cargoQuantity ?? selectedPortCall.cargoQuantity ?? null,
			type: 'number',
			editable: true,
			inputProps: { tabIndex: 0 },
		},
		{
			key: 'cargoUnits',
			label: 'Cargo Units',
			value: currentUnits,
			type: 'select',
			editable: true,
			options: Object.values(CargoUnitTypes).map((c) => ({ label: CargoUnitLabels[c], value: c })),
			inputProps: { placeholder: 'Select cargo units', showSearch: true, tabIndex: 0 },
		},
	] : [];

	const saveEditCargoActivity = useCallback(
		async (patch: Partial<PortCallProps>) => {
			const attrs = Object.entries(patch)
				.filter(([key, value]) => value !== (selectedPortCall as any)[key])
				.reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});

			if (!Object.keys(attrs).length) {
				return;
			}

			try {
				await updatePortCall(currentVesselId, currentPortCallId, attrs, true);
				showSuccessNotification('Cargo info saved');
			} catch (e) {
				showErrorNotification('Could not save info', e as Error);
			}

			await refreshItinerary();
		},
		[refreshItinerary, selectedPortCall, currentVesselId, currentPortCallId],
	);

	return (
		<>
			<Row gutter={[8, 8]}>
				<Col span={12}>
					<PortCallDetails />
				</Col>
				<Col span={12}>
					<Card
						slim
						title="Actions"
						extra={(
							<Space>
								<Button
									loading={suppliersLoading || bunkerStemsLoading}
									onClick={() => setShowBunkerDrawer(true)}
									icon={(<FontAwesomeIcon icon={faGasPump as IconProp} />)}
								>
									Add Bunker Stem
								</Button>
							</Space>
						)}
					>
						<Table
							pagination={false}
							dataSource={sortedActions}
							locale={{ emptyText: 'No actions registrered' }}
							size="small"
							rowKey="id"
							expandable={{
								onExpand(expanded, record) {
									if (expanded) {
										setExpandedPortCallAction(record);
									} else {
										setExpandedPortCallAction(null);
									}
								},
								expandedRowRender: (record) => {
									const rob = record;

									return (
										<Flex vertical gap={16}>
											{record.event === PortActionTypes.BUNKER_STEM && (
												<Table
													pagination={false}
													columns={getBunkerStemTableColumns(
														suppliers,
													)}
													dataSource={bunkerStems ?? []}
												/>
											)}
											<Table
												size="small"
												pagination={false}
												rowKey="id"
												dataSource={sortByFuelGrade(rob?.RobBunkers ?? [])}
												columns={[
													{
														title: 'Fuel Grade',
														dataIndex: 'fuelGrade',
													},
													{
														title: 'Quantity',
														dataIndex: 'adjustedQuantity',
														render: (v) => v.toFixed(3),
													},
												]}
											/>
										</Flex>
									);
								},
								rowExpandable: (record) => {
									const rob = record;

									if (rob == null) {
										return false;
									}

									if (rob.RobBunkers.length === 0) {
										return false;
									}

									return true;
								},
							}}
							columns={[
								{
									title: 'Action',
									dataIndex: 'event',
									render: (a, row: ItineraryPortCallDtoActions[number]) => {
										const asNumber = Number(a);
										const isValid = Number.isInteger(asNumber);

										if (isValid) {
											const event = portActivities.find((act) => act.id === asNumber)?.name;

											return event;
										}

										const event = PortActionTypeLabels[a as keyof typeof PortActionTypeLabels] ??
											capitalize(a);

										const estimated = getPortCallActionStatus({
											...row,
											PortCall: selectedPortCall,
										});

										if (estimated) {
											return (
												<>
													<Tag color="blue" style={{ marginRight: 8 }}>
														Estimated
													</Tag>
													{event}
												</>
											);
										}

										return event;
									},
								},
								{
									title: 'Date',
									dataIndex: 'date',
									render: (d, row) => {
										if (!d) {
											return 'N/A';
										}

										const offMin = getUtcOffset((row as any)?.port, new Date(d)) ?? 0;
										const m = toMoment(d).utc().utcOffset(offMin);

										return formatDate(m, DATE_AND_TIME, true, LocalTime);
									},
								},
								{
									title: 'Cargo',
									dataIndex: 'cargoId',
									render: (cargoId) => {
										if (cargoId == null) {
											return ' - ';
										}

										const cargo = (cargos ?? []).find((cg) => cg.id === cargoId);

										if (cargo == null) {
											return ' - ';
										}

										return cargo.type;
									},
								},
								{
									title: '',
									dataIndex: 'id',
									align: 'right' as const,
									render: (id, row) => id > 0 && (
										<Space>
											<Button
												disabled={unEditableActions.some((a) => a === row.event)}
												onClick={() => {
													if (row.event === PortActionTypes.BUNKER_STEM) {
														form.resetFields();

														const stem = (bunkerStems ?? []).find((bs) => (
															bs.id === row.bunkerStemId
														));

														if (stem != null) {
															setEditingStem(stem);
															setShowBunkerDrawer(true);
														}
													} else {
														setEditingActionId(id);
													}
												}}
												type="link"
												icon={(<EditOutlined />)}
											/>
											<Button
												disabled={unEditableActions.some((a) => a === row.event)}
												onClick={() => onDelete(id)}
												danger
												confirmTitle="Are you sure you want to delete this action and its associated ROB's?"
												type="link"
												icon={(<DeleteOutlined />)}
											/>
										</Space>
									),
								},
							]}
						/>
					</Card>
					<PortCallActivities
						selectedPortCall={selectedPortCall}
						vesselId={currentVesselId}
						onRefresh={() => {
							refreshItinerary();
							refreshStems();
							queryClient.invalidateQueries(['cargos']);
						}}
					/>
					<Space direction="vertical" size={16} style={{ width: '100%' }}>
						{isTcOrBbOut && (
							<EditableDetails
								title="Cargo activty"
								items={cargoActivity as any}
								onSave={() => {
									saveEditCargoActivity(editingCargoActivityValues);
									setEditingCargoActivityValues({});
								}}
								onEditValuesChange={(values: Partial<PortCallProps> | undefined) => {
									setEditingCargoActivityValues({ ...editingCargoActivityValues, ...values });
								}}
							/>
						)}
					</Space>
				</Col>
			</Row>
			<Drawer
				width={500}
				title={`${editingAction ? 'Update' : 'Create'} action in ${currentPort.name}`}
				open={showActivityModal || editingAction != null}
				onClose={onClose}
			>
				<Form
					layout="vertical"
					form={form}
				>
					<Form.Item required label="Action" name="event">
						{editingAction ? (
							<Input disabled value={editingAction.event} />
						) : (
							<Select
								showSearch
								disabled={editingAction != null}
								placeholder="Select action"
								options={
									portActivities.map((a: PortActivityProps) => ({
										label: a.name,
										value: a.name,
									}))
								}
							/>
						)}
					</Form.Item>
					<ActivityWithRobsForm
						key={editingActionId}
						afterOnSave={onClose}
						// @ts-ignore
						editingAction={editingAction}
						form={form}
					/>
				</Form>
			</Drawer>
			<CreateBunkerStemForm
				voyageId={voyageDetails?.id}
				refreshDetails={() => {
					refreshStems();
					refreshDetails();
					refreshItinerary();
					refreshVendorExpenses();
					queryClient.invalidateQueries(['cargos']);
				}}
				selectedPortCall={selectedPortCall}
				open={showBunkerDrawer}
				onClose={() => {
					setShowBunkerDrawer(false);
					refreshSuppliers();
					form.resetFields();
				}}
				tcInContract={voyageDetails?.tcInContract}
				editingStem={editingStem}
				setEditingStem={setEditingStem}
				fixtureCurrency={voyageDetails.bankAccount.currency}
			/>
		</>
	);
};

export default PortCallSummaryTab;
